'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';
import { useHomepageStats } from '@/hooks/useHomepageStats';

interface Metric {
  id: number;
  label: string;
  value: string;
  icon: string;
  color: string;
}

const PlatformMetrics = () => {
  const { t } = useTranslation();
  const { stats, loading, error } = useHomepageStats();

  // Format numbers for display
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M+`;
    } else if (num >= 1000) {
      return num.toLocaleString();
    }
    return num.toString();
  };

  const metrics: Metric[] = [
    {
      id: 1,
      label: t('activeManufacturers'),
      value: loading ? '...' : formatNumber(stats.activeManufacturers),
      icon: 'BuildingOfficeIcon',
      color: 'text-primary',
    },
    {
      id: 2,
      label: t('productListings'),
      value: loading ? '...' : formatNumber(stats.productListings),
      icon: 'CubeIcon',
      color: 'text-accent',
    },
    {
      id: 3,
      label: t('successfulConnections'),
      value: loading ? '...' : formatNumber(stats.successfulConnections),
      icon: 'LinkIcon',
      color: 'text-success',
    },
    {
      id: 4,
      label: t('averageResponseTime'),
      value: loading ? '...' : `${stats.averageResponseTime} hrs`,
      icon: 'ClockIcon',
      color: 'text-info',
    },
  ];

  if (error) {
    console.error('Error loading platform metrics:', error);
  }

  return (
    <section className="py-16 px-6 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {metrics.map((metric) => (
            <div
              key={metric.id}
              className="bg-background border border-border rounded-lg p-6 text-center hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center">
                  <Icon
                    name={metric.icon as any}
                    size={28}
                    variant="outline"
                    className={metric.color}
                  />
                </div>
              </div>
              <div className="text-3xl font-headline font-bold text-foreground mb-2">
                {metric.value}
              </div>
              <div className="text-sm text-muted-foreground">{metric.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PlatformMetrics;